import { Loading } from '@/components/Loading'
import { Icon } from '@/icons'
import { MktuClassShort, RegistryShort } from '@/services/Api/generated'
import { MktuButton } from '@/shared/Mktu/components/MktuButton'
import { DetailsPropInfo } from '@/shared/OnlineSearch/components/DetailsPropInfo'
import { MktuClassStore } from '@/store/Mktu/MktuClass'
import { mktuClassesList } from '@/store/Mktu/MktuClassesList'
import { format } from '@/utils/dates/functions'
import cn from 'classnames'
import { observer } from 'mobx-react-lite'
import Image from 'next/image'
import { FunctionComponent, useMemo } from 'react'
import { MktuClassDescriptionPopup } from '../MktuClassDescriptionPopup'
import styles from './DetailsPopup.module.scss'

interface DetailsPopupProps {
  onHide: () => void
  registryInfo?: RegistryShort
  isLoading: boolean
}
export const DetailsPopup: FunctionComponent<DetailsPopupProps> = observer(
  ({ onHide, registryInfo, isLoading }) => {
    const imageUrl = useMemo((): string | undefined => {
      if (registryInfo?.image?.fileUrl != null) {
        return registryInfo.image.fileUrl
      }
    }, [registryInfo])

    const dates = useMemo(() => {
      const {
        expiryDate,
        priorityDate,
        applicationDate,
        publicationDate,
        registrationDate,
      } = registryInfo?.dates ?? {}

      function formatDate(date?: Date): string {
        return date != null ? format(date, 'DD.MM.YYYY') : '--'
      }

      return {
        expiryDate: formatDate(expiryDate),
        priorityDate: formatDate(priorityDate),
        applicationDate: formatDate(applicationDate),
        publicationDate: formatDate(publicationDate),
        registrationDate: formatDate(registrationDate),
      }
    }, [registryInfo])

    const mktuClasses = useMemo(() => {
      return (registryInfo?.mktuClasses ?? [])
        .map((shortInfo: MktuClassShort) => {
          const mktuClass = mktuClassesList.classes.find(
            (mktuClassStore) => mktuClassStore.number === shortInfo.classNum
          )
          const cloneMktuClass = new MktuClassStore(
            mktuClass.mktuClass,
            mktuClass.words
          )
          const words = shortInfo.text?.slice(0, -1).split('; ') ?? []

          cloneMktuClass.setSelectedWordsNames(words)

          return cloneMktuClass
        })
        .filter((item): item is MktuClassStore => item != null)
    }, [registryInfo])

    return (
      <>
        <div
          className={cn(styles.background, {
            [styles.isVisible]: true,
          })}
          onClick={onHide}
        ></div>
        <div
          className={cn(styles.container, {
            [styles.isVisible]: true,
          })}
        >
          {isLoading ? (
            <>
              <div className={styles.header}>
                <p className={styles.title}>Загрузка...</p>
                <button className={styles.close} onClick={onHide}>
                  <Icon name="close" className={styles.closeIcon} />
                </button>
              </div>
              <Loading />
              <div className={styles.postLoader}></div>
            </>
          ) : (
            <>
              <div className={styles.header}>
                <p className={styles.title}>
                  Товарный знак №{registryInfo.registrationNumber}
                </p>
                <button className={styles.close} onClick={onHide}>
                  <Icon name="close" className={styles.closeIcon} />
                </button>
                <p className={styles.valid}>
                  <span className={styles.validText}>
                    Действует до{' '}
                    <span className={styles.validTextDate}>
                      {dates.expiryDate}
                    </span>
                  </span>
                </p>
              </div>
              <div className={styles.contentBlock}>
                <div className={styles.content}>
                  <div className={styles.imageBlock}>
                    <Image
                      className={styles.imageBlockImage}
                      src={imageUrl}
                      fill
                      alt={registryInfo.rightholderName}
                      unoptimized
                    />
                  </div>
                  <div className={styles.details}>
                    <DetailsPropInfo
                      name="Правообладатель"
                      value={registryInfo.rightholderName ?? ''}
                    />
                    <DetailsPropInfo
                      name="Адрес"
                      value={registryInfo.rightholderAddress ?? ''}
                    />
                    <div className={styles.detailsGroup}>
                      <DetailsPropInfo
                        name="Приоритет товарного знака"
                        value={dates.priorityDate}
                      />
                      <DetailsPropInfo
                        name="Дата подачи заявки"
                        value={dates.applicationDate}
                      />
                    </div>
                    <div className={styles.detailsGroup}>
                      <DetailsPropInfo
                        name="Дата государственной регистрации"
                        value={dates.registrationDate}
                      />
                      <DetailsPropInfo
                        name="Дата публикации"
                        value={dates.publicationDate}
                      />
                    </div>
                    <DetailsPropInfo
                      name="Неохраняемые элементы товарного знака"
                      value={registryInfo.unprotectedElements ?? 'Нет данных'}
                    />
                  </div>
                </div>

                <div className={styles.mktuClassBlock}>
                  <p className={styles.mktuClassBlockText}>
                    Классы МКТУ и перечень товаров/услуг:
                  </p>
                  <div className={styles.mktuClasses}>
                    {mktuClasses.map((mktuClassStore) => (
                      <MktuButton
                        key={mktuClassStore.number}
                        mktuClass={mktuClassStore.mktuClass}
                        disableLink={true}
                      >
                        {({ refs, floatingStyles, isPopupVisible }) => (
                          <MktuClassDescriptionPopup
                            ref={refs.setFloating}
                            mktuClassStore={mktuClassStore}
                            isVisible={isPopupVisible}
                            style={floatingStyles}
                            registrationNumber={registryInfo.registrationNumber}
                          />
                        )}
                      </MktuButton>
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </>
    )
  }
)
